import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button, Card } from '../../design-system';
import { resolveTenant, TenantPublicInfo } from '../../services/consentApi';
import { 
  getConsentHistory, 
  getPortalToken, 
  getPortalUser, 
  isPortalSessionValid,
  clearPortalSession,
  portalLogout,
  ConsentHistoryItem 
} from '../../services/portalApi';
import { useI18n } from '../../i18n';
import '../../design-system/styles.css';

type FilterStatus = 'ALL' | 'ACTIVE' | 'WITHDRAWN' | 'EXPIRED';

/**
 * Data Owner Consent History Page
 * Route: /t/:tenantSlug/my-consent/history
 */
const DataOwnerConsentHistory: React.FC = () => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();
  const { t, language, setLanguage, supportedLanguages } = useI18n();

  // Tenant state
  const [tenant, setTenant] = useState<TenantPublicInfo | null>(null);
  
  // Data state
  const [consents, setConsents] = useState<ConsentHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter state
  const [filter, setFilter] = useState<FilterStatus>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // User info
  const user = getPortalUser();

  // Check session and load data
  useEffect(() => {
    const checkSessionAndLoad = async () => {
      // Check if session is valid
      if (!isPortalSessionValid()) {
        clearPortalSession();
        navigate(`/t/${tenantSlug}/my-consent/login`);
        return;
      }

      const token = getPortalToken();
      const currentUser = getPortalUser();
      if (!token || !currentUser) {
        navigate(`/t/${tenantSlug}/my-consent/login`);
        return;
      }

      try {
        // Load tenant info and consent history in parallel
        const [tenantInfo, consentData] = await Promise.all([
          resolveTenant(tenantSlug!),
          getConsentHistory(token, currentUser.tenantId, currentUser.dataOwnerId),
        ]);
        
        setTenant(tenantInfo);
        setConsents(consentData);
        setError(null);
      } catch (err: any) {
        if (err.response?.status === 401) {
          clearPortalSession();
          navigate(`/t/${tenantSlug}/my-consent/login`);
          return;
        }
        setError(err.response?.data?.message || 'Failed to load consent history');
      } finally {
        setLoading(false);
      }
    };

    checkSessionAndLoad();
  }, [tenantSlug, navigate]);

  // Handle logout
  const handleLogout = useCallback(async () => {
    const token = getPortalToken();
    if (token) {
      try {
        await portalLogout(token);
      } catch {
        // Ignore logout errors
      }
    }
    clearPortalSession();
    navigate(`/t/${tenantSlug}/my-consent/login`);
  }, [tenantSlug, navigate]);

  // Filter consents by status, search, and date range
  const filteredConsents = consents.filter(consent => {
    // Status filter
    if (filter !== 'ALL' && consent.status !== filter) return false;
    // Search filter (project name, template name, data categories, data collector)
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchesProject = consent.projectName?.toLowerCase().includes(q);
      const matchesTemplate = consent.templateName?.toLowerCase().includes(q);
      const matchesCollector = consent.dataCollectorName?.toLowerCase().includes(q);
      const matchesCategory = consent.dataCategories?.some((c: string) => c.toLowerCase().includes(q));
      if (!matchesProject && !matchesTemplate && !matchesCollector && !matchesCategory) return false;
    }
    // Date range filter
    if (dateFrom && consent.submittedAt) {
      if (new Date(consent.submittedAt) < new Date(dateFrom)) return false;
    }
    if (dateTo && consent.submittedAt) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (new Date(consent.submittedAt) > toDate) return false;
    }
    return true;
  });

  // CSV export handler
  const handleExportCsv = () => {
    const headers = ['Consent ID', 'Project', 'Template', 'Data Collector', 'Data Categories', 'Status', 'Consented Date', 'Expiry Date', 'Withdrawal Date'];
    const rows = filteredConsents.map(c => [
      c.consentId, c.projectName || '', c.templateName || '', c.dataCollectorName || '',
      (c.dataCategories || []).join('; '), c.status,
      c.submittedAt || '', c.expiresAt || '', c.withdrawnAt || '',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'consent-history.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Get status badge style
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return { background: 'var(--ds-success-light)', color: 'var(--ds-success)' };
      case 'WITHDRAWN':
        return { background: 'var(--ds-warning-light)', color: 'var(--ds-warning)' };
      case 'EXPIRED':
        return { background: 'var(--ds-bg-secondary)', color: 'var(--ds-text-muted)' };
      default:
        return { background: 'var(--ds-bg-secondary)', color: 'var(--ds-text-muted)' };
    }
  };

  // Get status label
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return t.portal?.statusActive || 'Active';
      case 'WITHDRAWN':
        return t.portal?.statusWithdrawn || 'Withdrawn';
      case 'EXPIRED':
        return t.portal?.statusExpired || 'Expired';
      default:
        return status;
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString(language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  // Loading state
  if (loading) {
    return (
      <main className="ds-container" style={{ textAlign: 'center', paddingTop: 100 }}>
        <p className="ds-text-muted">{t.common.loading}</p>
      </main>
    );
  }

  return (
    <main style={{ minHeight: '100vh', background: 'var(--ds-bg-secondary)' }} role="main" aria-labelledby="page-title">
      {/* Skip to main content link */}
      <a 
        href="#main-content" 
        className="sr-only"
        style={{ position: 'absolute' }}
        onFocus={(e) => {
          e.currentTarget.classList.remove('sr-only');
          e.currentTarget.style.position = 'fixed';
          e.currentTarget.style.top = '10px';
          e.currentTarget.style.left = '10px';
          e.currentTarget.style.padding = '12px 24px';
          e.currentTarget.style.background = 'var(--tashi-blue-grey-9)';
          e.currentTarget.style.color = 'white';
          e.currentTarget.style.borderRadius = '8px';
          e.currentTarget.style.zIndex = '9999';
          e.currentTarget.style.fontWeight = '600';
        }}
        onBlur={(e) => {
          e.currentTarget.classList.add('sr-only');
        }}
      >
        Skip to main content
      </a>

      {/* Header */}
      <header style={{ 
        background: 'linear-gradient(135deg, var(--tashi-blue-grey-9) 0%, var(--tashi-blue-grey-7) 100%)', 
        padding: '16px 24px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}>
        <div style={{ 
          maxWidth: 1200, 
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {tenant?.logo ? (
              <img src={tenant.logo} alt="" style={{ height: 36, borderRadius: 6 }} aria-hidden="true" />
            ) : (
              <span aria-hidden="true" style={{ 
                width: 36, height: 36, 
                background: 'rgba(255,255,255,0.2)', 
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
              }}>🔐</span>
            )}
            <div>
              <h1 id="page-title" style={{ fontSize: 18, margin: 0, fontWeight: 600, color: 'white' }}>
                {t.portal?.myConsents || 'My Consents'}
              </h1>
              {tenant && (
                <p style={{ fontSize: 14, margin: 0, color: 'rgba(255,255,255,0.9)' }}>
                  {tenant.name}
                </p>
              )}
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Language selector */}
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value as any)}
              aria-label="Select language"
              style={{
                padding: '8px 12px',
                borderRadius: 6,
                border: '2px solid transparent',
                fontSize: 14,
                background: 'rgba(255,255,255,0.15)',
                color: 'white',
                cursor: 'pointer',
              }}
            >
              {supportedLanguages.map((lang) => (
                <option key={lang.code} value={lang.code} style={{ color: '#333' }}>
                  {lang.nativeName}
                </option>
              ))}
            </select>
            
            {/* Logout button */}
            <button
              onClick={handleLogout}
              aria-label={t.portal?.logout || 'Logout'}
              style={{
                padding: '8px 14px',
                borderRadius: 6,
                border: '2px solid transparent',
                background: 'rgba(255,255,255,0.15)',
                color: 'white',
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              {t.portal?.logout || 'Logout'}
            </button>
          </div>
        </div>
      </header>

      <div id="main-content" style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
        {/* Welcome card with summary */}
        {user && (
          <Card style={{ 
            marginBottom: 24, 
            padding: 24,
            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            border: '1px solid var(--ds-border)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
              <div aria-hidden="true" style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 600,
                fontSize: 18,
              }}>
                {user.firstName?.charAt(0).toUpperCase()}
              </div>
              <div>
                <p style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                  {t.portal?.welcomeBack || 'Welcome back'}, {user.firstName}!
                </p>
                <p style={{ margin: 0, fontSize: 14, color: 'var(--ds-text-muted)' }}>
                  {user.email}
                </p>
              </div>
            </div>
            
            {/* Stats row */}
            <section aria-label="Consent statistics" style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: 12,
              padding: '16px 0',
              borderTop: '1px solid var(--ds-border)',
            }}>
              <div style={{ textAlign: 'center' }}>
                <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--ds-success)' }}>
                  {consents.filter(c => c.status === 'ACTIVE').length}
                </p>
                <p style={{ margin: 0, fontSize: 14, color: 'var(--ds-text-muted)' }}>
                  {t.portal?.statusActive || 'Active'}
                </p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--ds-warning)' }}>
                  {consents.filter(c => c.status === 'WITHDRAWN').length}
                </p>
                <p style={{ margin: 0, fontSize: 14, color: 'var(--ds-text-muted)' }}>
                  {t.portal?.statusWithdrawn || 'Withdrawn'}
                </p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--ds-text-muted)' }}>
                  {consents.filter(c => c.status === 'EXPIRED').length}
                </p>
                <p style={{ margin: 0, fontSize: 14, color: 'var(--ds-text-muted)' }}>
                  {t.portal?.statusExpired || 'Expired'}
                </p>
              </div>
            </section>
          </Card>
        )}

        {/* Error message - aria-live for screen readers */}
        <div aria-live="polite" aria-atomic="true">
          {error && (
            <div className="ds-alert ds-alert--error" role="alert" style={{ marginBottom: 16 }}>
              {error}
              <button
                onClick={() => window.location.reload()}
                style={{
                  marginLeft: 12,
                  background: 'none',
                  border: 'none',
                  color: 'inherit',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                {t.common.retry}
              </button>
            </div>
          )}
        </div>

        {/* Search, date filter, and export */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            placeholder={'Search by project, template, or category...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: '1 1 200px', padding: '10px 14px', borderRadius: 8,
              border: '1px solid var(--ds-border)', fontSize: 14, background: 'white',
            }}
            aria-label="Search consents"
          />
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid var(--ds-border)', fontSize: 14 }}
            aria-label="From date"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid var(--ds-border)', fontSize: 14 }}
            aria-label="To date"
          />
          <Button variant="ghost" onClick={handleExportCsv} style={{ whiteSpace: 'nowrap' }}>
            Download CSV
          </Button>
        </div>

        {/* Filter tabs */}
        <nav aria-label="Filter consents" style={{
          display: 'flex',
          gap: 8,
          marginBottom: 16,
          overflowX: 'auto',
          paddingBottom: 4,
        }}>
          {(['ALL', 'ACTIVE', 'WITHDRAWN', 'EXPIRED'] as FilterStatus[]).map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              aria-pressed={filter === status}
              style={{
                padding: '10px 16px',
                borderRadius: 20,
                border: '2px solid transparent',
                background: filter === status ? 'var(--ds-accent)' : 'white',
                color: filter === status ? 'white' : 'var(--ds-text)',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: filter === status ? 600 : 400,
                whiteSpace: 'nowrap',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              {status === 'ALL' ? (t.portal?.filterAll || 'All') : getStatusLabel(status)}
              {status === 'ALL' && ` (${consents.length})`}
            </button>
          ))}
        </nav>

        {/* Consent list */}
        {filteredConsents.length === 0 ? (
          <Card style={{ padding: 32, textAlign: 'center' }}>
            <span aria-hidden="true" style={{ fontSize: 48, display: 'block', marginBottom: 16 }}>📋</span>
            <p style={{ margin: 0, color: 'var(--ds-text-muted)', fontSize: 16 }}>
              {filter === 'ALL' 
                ? (t.portal?.noConsents || 'No consent artifacts found')
                : (t.portal?.noConsentsWithFilter || 'No consents with this status')
              }
            </p>
          </Card>
        ) : (
          <section aria-label="Consent artifacts">
            <h2 className="sr-only">Consent Artifacts</h2>
            <ul style={{ 
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', 
              gap: 16 
            }}>
              {filteredConsents.map((consent) => (
                <li key={consent.consentId}>
                  <Card style={{ padding: 20, height: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                      <div>
                        <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 600 }}>
                          {consent.projectName || consent.templateName}
                        </h3>
                        <p style={{ margin: 0, fontSize: 14, color: 'var(--ds-text-muted)' }}>
                          {t.portal?.consentedOn || 'Consented on'}: {consent.submittedAt ? formatDate(consent.submittedAt) : 'N/A'}
                        </p>
                      </div>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: 12,
                        fontSize: 14,
                        fontWeight: 600,
                        ...getStatusStyle(consent.status),
                      }}>
                        {getStatusLabel(consent.status)}
                      </span>
                    </div>

                {consent.dataCollectorName && (
                  <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--ds-text-secondary)' }}>
                    {consent.dataCollectorName}
                  </p>
                )}

                {consent.templateName && consent.templateName !== consent.projectName && (
                  <p style={{
                    margin: '0 0 8px',
                    fontSize: 14,
                    color: 'var(--ds-text-secondary)',
                    lineHeight: 1.4,
                  }}>
                    Template: {consent.templateName}
                  </p>
                )}

                {consent.dataCategories && consent.dataCategories.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
                    {consent.dataCategories.map((cat: string, i: number) => (
                      <span key={i} style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: 12,
                        background: 'var(--ds-accent-light, #e8f4fd)', color: 'var(--ds-accent, #1a73e8)',
                        fontWeight: 500,
                      }}>{cat}</span>
                    ))}
                  </div>
                )}

                {consent.hasPendingWithdrawal && (
                  <p style={{ 
                    margin: '0 0 12px', 
                    fontSize: 14, 
                    color: 'var(--ds-warning)',
                    fontWeight: 500,
                  }}>
                    <span aria-hidden="true">⏳</span> {'Withdrawal request pending'}
                  </p>
                )}

                {consent.expiresAt && consent.status === 'ACTIVE' && (
                  <p style={{ 
                    margin: '0 0 12px', 
                    fontSize: 14, 
                    color: 'var(--ds-text-muted)' 
                  }}>
                    {t.portal?.expiresOn || 'Expires on'}: {formatDate(consent.expiresAt)}
                  </p>
                )}

                {consent.withdrawnAt && (
                  <p style={{ 
                    margin: '0 0 12px', 
                    fontSize: 14, 
                    color: 'var(--ds-warning)' 
                  }}>
                    {t.portal?.withdrawnOn || 'Withdrawn on'}: {formatDate(consent.withdrawnAt)}
                  </p>
                )}

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  {consent.canWithdraw && !consent.hasPendingWithdrawal && (
                    <Button
                      variant="ghost"
                      onClick={() => navigate(`/t/${tenantSlug}/my-consent/withdraw/${consent.consentId}`)}
                      style={{ flex: 1, padding: '12px 16px', fontSize: 14 }}
                    >
                      {t.portal?.withdrawConsent || 'Withdraw Consent'}
                    </Button>
                  )}
                </div>
                  </Card>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Bottom navigation */}
        <nav style={{ 
          display: 'flex', 
          gap: 12, 
          marginTop: 24,
          padding: '16px 0',
        }}>
          <Link 
            to={`/t/${tenantSlug}/my-consent/withdrawals`}
            style={{
              flex: 1,
              padding: '14px 16px',
              borderRadius: 8,
              background: 'white',
              color: 'var(--ds-text)',
              textDecoration: 'none',
              textAlign: 'center',
              fontSize: 14,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            📝 {t.portal?.withdrawalHistory || 'Withdrawal History'}
          </Link>
          <Link 
            to={`/t/${tenantSlug}/my-consent/help`}
            style={{
              flex: 1,
              padding: '14px 16px',
              borderRadius: 8,
              background: 'white',
              color: 'var(--ds-text)',
              textDecoration: 'none',
              textAlign: 'center',
              fontSize: 14,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            ❓ {t.portal?.help || 'Help'}
          </Link>
        </nav>
      </div>
    </main>
  );
};

export default DataOwnerConsentHistory;
